import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Logo } from '../Logo';

/**
 * Shared Navbar component for all marketing/public pages
 * Provides consistent navigation across Landing, About, Contact, Terms, Privacy pages
 */
export const MarketingNavbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <Logo size="default" />
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</Link>
            <Link to="/#pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/about" className="text-sm text-slate-400 hover:text-white transition-colors">About</Link>
            <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white">
                Log in
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Start Free Trial
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-slate-300"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden bg-slate-900 border-t border-slate-800"
          >
            <div className="px-4 py-4 space-y-3">
              <Link to="/#features" className="block text-slate-300 hover:text-white py-2">Features</Link>
              <Link to="/#pricing" className="block text-slate-300 hover:text-white py-2">Pricing</Link>
              <Link to="/about" className="block text-slate-300 hover:text-white py-2">About</Link>
              <Link to="/contact" className="block text-slate-300 hover:text-white py-2">Contact</Link>
              <div className="pt-4 border-t border-slate-800 space-y-2">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full border-slate-700">Log in</Button>
                </Link>
                <Link to="/signup" className="block">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">Start Free Trial</Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

/**
 * Shared Footer component for all marketing/public pages
 * Provides consistent footer across Landing, About, Contact, Terms, Privacy pages
 */
export const MarketingFooter = () => (
  <footer className="bg-slate-900 border-t border-slate-800 py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8">
        {/* Logo & Description */}
        <div className="md:col-span-1">
          <Link to="/" className="flex items-center gap-3 mb-4">
            <Logo size="default" />
          </Link>
          <p className="text-sm text-slate-400">
            Multi-layer cybersecurity platform for modern businesses.
          </p>
        </div>

        {/* Product Links */}
        <div>
          <h4 className="font-medium text-white mb-4">Product</h4>
          <ul className="space-y-2 text-sm text-slate-400">
            <li><Link to="/#features" className="hover:text-white transition-colors">Features</Link></li>
            <li><Link to="/#pricing" className="hover:text-white transition-colors">Pricing</Link></li>
          </ul>
        </div>

        {/* Company Links */}
        <div>
          <h4 className="font-medium text-white mb-4">Company</h4>
          <ul className="space-y-2 text-sm text-slate-400">
            <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
            <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
          </ul>
        </div>

        {/* Legal Links */}
        <div>
          <h4 className="font-medium text-white mb-4">Legal</h4>
          <ul className="space-y-2 text-sm text-slate-400">
            <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
          </ul>
        </div>
      </div>

      <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-sm text-slate-500">
          © {new Date().getFullYear()} Clear Defense AI. All rights reserved.
        </p>
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="border-slate-700 text-slate-400">
            SOC 2 Certified
          </Badge>
          <Badge variant="outline" className="border-slate-700 text-slate-400">
            GDPR Compliant
          </Badge>
        </div>
      </div>
    </div>
  </footer>
);

/**
 * Main Marketing Page Layout component
 * Wraps page content with consistent Navbar and Footer
 * 
 * @param {React.ReactNode} children - Page content to render between navbar and footer
 */
export default function MarketingPageLayout({ children }) {
  return (
    <div className="min-h-screen bg-slate-950">
      <MarketingNavbar />
      <main>
        {children}
      </main>
      <MarketingFooter />
    </div>
  );
}
